import { useApolloClient } from '@apollo/react-hooks'
import type {
  Contact,
  ContactBySourceQuery,
  ContactQuery,
  SearchContactDealsQuery,
  SearchContactOpportunitiesQuery
} from '@paintscout/api'
import {
  ContactBySourceDocument,
  ContactDocument,
  SearchContactDealsDocument,
  SearchContactOpportunitiesDocument
} from '@paintscout/api'
import { hasIntegrationInterface, uuid } from '@paintscout/util/builder'
import * as Sentry from '@sentry/react'
import omit from 'lodash/omit'
import { useSnackbar } from 'notistack'
import React, { useContext, useRef, useState } from 'react'

const getContactSourceDisplayName = (source: string): string => {
  switch (source) {
    case 'pipeline_deals':
      return 'Pipeline CRM'
    case 'service_titan':
      return 'Service Titan'
    case 'salesforce':
      return 'Salesforce'
    default:
      return 'Unknown Integration'
  }
}
import {
  ConflictResolveDialog,
  Dialog,
  DialogContent,
  DialogStackContext,
  DialogTitle,
  SelectContactDetailsDialog,
  SelectDealDialog,
  SelectOpportunityDialog,
  useClientOptions,
  useUser,
  makeStyles
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import ClearIcon from '@material-ui/icons/Clear'
import type { Filter } from 'json-to-lucene'
import ContactSearch from '../../common/ContactSearch'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    dialogPaper: {
      overflow: 'visible'
    },
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center'
    }
  }),
  { name: 'ContactSearchDialog' }
)

export interface ContactSearchDialogProps {
  classes?: StyleClasses<typeof useStyles>

  onCreateNew?: (query: string) => any
  onConfirm?: (contact: Contact | Partial<Contact>, currentDealId?: number | string, providerName?: string) => any
  onCancel?: (event: React.MouseEvent<HTMLElement>) => any
  ExtraContent?: JSX.Element
  open?: boolean
  filters?: Filter[]
  blockIntegration?: boolean
}

function ContactSearchDialog({
  onConfirm,
  onCancel,
  onCreateNew: _onCreateNew,
  ExtraContent,
  open = true,
  filters = [],
  blockIntegration = false
}: ContactSearchDialogProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { preferences } = useUser()
  const { openDialog, dismissDialog, dismissAllDialogs } = useContext(DialogStackContext)
  const { enqueueSnackbar } = useSnackbar()
  const apolloClient = useApolloClient()
  const [isOpen, setIsOpen] = useState<boolean>(open)
  const [isFirstSearch, setIsFirstSearch] = useState(true)
  const fetchingContact = useRef<boolean>(false)
  const forceIntegrationContacts = options.options.quotes.forceIntegrationContacts
  const onCreateNew = forceIntegrationContacts ? null : _onCreateNew

  const completeContactSelect = async ({
    fullContact,
    dealIds
  }: {
    fullContact: Contact
    dealIds?: Contact['pldDeals']
  }) => {
    if (dealIds.length > 0 && fullContact.source_id) {
      const deals = await apolloClient
        .query<SearchContactDealsQuery>({
          query: SearchContactDealsDocument,
          variables: { contactSourceId: fullContact.source_id }
        })
        .then((res) => res.data?.searchContactDeals?.results ?? [])

      setIsOpen(false)
      return handleSelectDeal(deals, fullContact)
    }

    if (fullContact.source === 'salesforce' && fullContact.source_user_id) {
      const opportunities = await apolloClient
        .query<SearchContactOpportunitiesQuery>({
          query: SearchContactOpportunitiesDocument,
          variables: { contactSourceId: fullContact.source_user_id }
        })
        .then((res) => res.data?.searchContactOpportunities?.results ?? [])

      setIsOpen(false)
      return handleSelectOpportunity(opportunities, fullContact)
    }

    if (fullContact.source === 'service_titan') {
      setIsOpen(false)
      return handleSelectContactDetails(omit(fullContact, 'company'))
    }

    const selectedDealId = dealIds[0]?.id ?? null
    return onConfirm(fullContact, selectedDealId)
  }

  const handleSelectContact = async (
    selectedContact: (Contact & { dealId?: number }) | (Partial<Contact> & { dealId?: number })
  ) => {
    const id = selectedContact._id

    if ((selectedContact.source_id || selectedContact.source_user_id) && !id) {
      fetchingContact.current = true
      const contact = await apolloClient
        .query<ContactBySourceQuery>({
          query: ContactBySourceDocument,
          fetchPolicy: 'no-cache',
          variables: {
            source: selectedContact.source,
            source_id: `${selectedContact.source_id}`
          }
        })
        .then((res) => res.data.contactBySource as Contact)
        .catch((error) => {
          Sentry.captureException(error, { tags: { contactId: id } })
          return null
        })
      fetchingContact.current = false

      const dealIds = selectedContact?.pldDeals ?? []
      if (contact && selectedContact) {
        const updatedFields = checkContactUpdated({ existing: contact, current: selectedContact })

        if (updatedFields.length > 0) {
          const updates = updatedFields.map(
            (field) =>
              `${fieldNameTranslate[field]} was changed from "${contact[field]}" to "${selectedContact[field]}"`
          )
          openDialog(ConflictResolveDialog, {
            title: 'Merge Contact Updates',
            message: `The selected contact has been updated in ${getContactSourceDisplayName(contact.source)}. Do you want to update the existing contact with the new details?`,
            updates,
            yesLabel: 'Yes',
            noLabel: 'No',
            scary: true,
            onConfirm: async () => {
              const fullContact = {
                ...contact,
                ...updatedFields.reduce((acc, field) => {
                  acc[field] = selectedContact[field]
                  return acc
                }, {})
              }
              dismissDialog()
              await completeContactSelect({ fullContact, dealIds })
            },
            onCancel: async () => {
              dismissDialog()
              await completeContactSelect({ fullContact: contact, dealIds })
            }
          })
        } else {
          dismissDialog()
          await completeContactSelect({ fullContact: contact, dealIds })
        }
      } else {
        const fullContact = contact ?? { _id: uuid(), ...selectedContact }
        await completeContactSelect({ fullContact, dealIds })
      }
    } else {
      try {
        if (id) {
          fetchingContact.current = true
          const contact = await apolloClient
            .query<ContactQuery>({
              query: ContactDocument,
              fetchPolicy: 'no-cache',
              variables: { id }
            })
            .then((res) => res.data.contact)

          fetchingContact.current = false
          return onConfirm(contact)
        }
        return onConfirm({ _id: uuid(), ...selectedContact })
      } catch (e) {
        console.error(e)
        enqueueSnackbar('An error occurred while getting this Contact', { variant: 'error' })
      }
    }
  }

  const handleSelectOpportunity = async (opportunities: Contact['salesforceOpportunities'][], contact: Contact) => {
    openDialog(
      SelectOpportunityDialog,
      {
        opportunities,
        onConfirm: (opportunityId: string) => {
          dismissDialog()
          return opportunityId === 'none' ? onConfirm(contact) : onConfirm(contact, opportunityId)
        },
        onCancel: () => {
          setIsOpen(true)
          dismissDialog()
        }
      },
      { replace: true }
    )
  }

  const handleSelectDeal = async (deals: any[], contact: Contact) => {
    openDialog(
      SelectDealDialog,
      {
        deals,
        contact,
        onConfirm: (dealId: string) => {
          dismissDialog()
          return dealId === 'none' ? onConfirm(contact) : onConfirm(contact, Number(dealId))
        },
        onCancel: () => {
          setIsOpen(true)
          dismissDialog()
        }
      },
      { replace: false }
    )
  }

  const handleSelectContactDetails = async (contact: Contact) => {
    openDialog(
      SelectContactDetailsDialog,
      {
        contactId: contact.source_user_id,
        onConfirm: (values: Record<string, string>) => {
          dismissAllDialogs()
          return onConfirm({ ...contact, ...values }, Number(contact.source_id), 'serviceTitan')
        },
        onCancel: () => {
          setIsOpen(true)
          dismissDialog()
        }
      },
      { replace: false }
    )
  }

  const rightContent = (
    <div onClick={onCancel} className={classes.closeIcon}>
      <ClearIcon />
    </div>
  )
  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="md" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle loading={fetchingContact.current} rightContent={rightContent}>
        Select a Contact
      </DialogTitle>
      <DialogContent className={classes.dialogPaper}>
        {ExtraContent}
        <ContactSearch
          filters={filters}
          onCreateNew={onCreateNew}
          onSelectContact={handleSelectContact}
          isFirstSearch={isFirstSearch}
          setIsFirstSearch={setIsFirstSearch}
          integrations={{
            paintscout: !forceIntegrationContacts,
            pipelineDeals:
              !blockIntegration && !!options.options.pipelineKey?.value && !!options.options?.pipelineKey?.enabled,
            serviceTitan:
              !blockIntegration && hasIntegrationInterface({ preferences, options, integration: 'serviceTitan' }),
            salesforce:
              !blockIntegration && hasIntegrationInterface({ preferences, options, integration: 'salesforce' })
          }}
          disabled={fetchingContact.current}
        />
      </DialogContent>
    </Dialog>
  )
}

const fieldNameTranslate = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  phone_number: 'Phone Number',
  address: 'Address',
  country: 'Country',
  city: 'City',
  postal: 'Postal',
  company: 'Company'
}

function checkContactUpdated({ existing, current }: { existing: Contact; current: Partial<Contact> }): string[] {
  const updatedFields: string[] = []
  const fieldsToCheck = [
    'first_name',
    'last_name',
    'email',
    'phone_number',
    'address',
    'country',
    'city',
    'postal',
    'company'
  ]
  fieldsToCheck.forEach((field) => {
    if (current[field] && existing[field] && existing[field] !== current[field]) {
      updatedFields.push(field)
    }
  })
  return updatedFields
}

export default ContactSearchDialog
